import { useEffect, useMemo, useState } from "react";
import { Link, useFetcher } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ChevronDown, ChevronRight, Info, Loader2 } from "lucide-react";
import { Section } from "@/components/layout-2/ds";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FormError } from "@/components/ui/form-error";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { ActionWrapper } from "@/utils/wretch";
import { DeployBlocker, DeployPreview, DeployTargetOption, InFlightPolicy } from "@/types/dialer/workflow";
import { ScenarioPill } from "./scenario-pill";

export type DeployTarget = DeployTargetOption;

export type DeployGate = {
    /** What actually stops the operator, given the mode. */
    blockers: DeployBlocker[];
    /** Scenario blockers, singled out because a rollback may ignore them. */
    scenarioBlockers: DeployBlocker[];
    canSubmit: boolean;
    /** The one remaining obstacle is scenarios, so the button offers to run them. */
    mustRunScenarios: boolean;
};

/**
 * The guardrail, as a function of the server's preview alone.
 *
 * A version whose scenarios fail cannot be deployed, and a version with no scenario
 * can be — the server says so through `blockers` and `warnings`, and the dialog only
 * has to obey. A rollback is exempt from the scenario blockers: the version it goes
 * back to already ran, and refusing to undo a bad deployment would be the worse bug.
 */
export function deployGate(preview: DeployPreview | undefined, mode: "deploy" | "rollback", hasTarget: boolean): DeployGate {
    const all = preview?.blockers ?? [];
    const scenarioBlockers = all.filter(blocker => blocker.code.startsWith("scenarios"));
    const blockers = mode === "rollback" ? all.filter(blocker => !scenarioBlockers.includes(blocker)) : all;
    return {
        blockers,
        scenarioBlockers,
        canSubmit: Boolean(preview) && blockers.length === 0 && hasTarget,
        mustRunScenarios: Boolean(preview) && blockers.length > 0 && blockers.every(blocker => blocker.code.startsWith("scenarios"))
    };
}

export type DeployDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    workflowCode: string;
    workflowName?: string | null;
    versionId: number;
    versionNumber: number;
    /** Campaign and slot candidates; the operator picks when there is more than one. */
    targets: DeployTarget[];
    mode?: "deploy" | "rollback";
    onDone?: () => void;
};

function targetKey(target: DeployTarget): string {
    return `${target.campaign_code}::${target.slot}`;
}

/**
 * Deployment, with the fate of in-flight cards as an explicit choice and the scenario
 * guardrail applied where the operator tries to act, not afterwards.
 */
export function DeployDialog({ open, onOpenChange, workflowCode, workflowName, versionId, versionNumber, targets, mode = "deploy", onDone }: DeployDialogProps) {
    const previewFetcher = useFetcher<{ preview: DeployPreview }>();
    const deployFetcher = useFetcher<ActionWrapper<unknown>>();

    const groupedTargets = useMemo(
        () => ({
            preferred: targets.filter(target => !target.current || target.current.workflow_code === workflowCode),
            switches: targets.filter(target => target.current && target.current.workflow_code !== workflowCode)
        }),
        [targets, workflowCode]
    );
    const [showSwitchTargets, setShowSwitchTargets] = useState(false);
    const visibleTargets = useMemo(() => (showSwitchTargets ? [...groupedTargets.preferred, ...groupedTargets.switches] : groupedTargets.preferred), [groupedTargets, showSwitchTargets]);
    const [selectedTarget, setSelectedTarget] = useState<string>(groupedTargets.preferred[0] ? targetKey(groupedTargets.preferred[0]) : "");
    const [policy, setPolicy] = useState<InFlightPolicy>("pin");
    const [showMissing, setShowMissing] = useState(false);
    const [replaying, setReplaying] = useState(false);

    const target = useMemo(() => visibleTargets.find(candidate => targetKey(candidate) === selectedTarget) ?? visibleTargets[0], [visibleTargets, selectedTarget]);

    const loadPreview = (refresh = false) => {
        if (!target) return;
        setReplaying(refresh);
        previewFetcher.load(`/outils/campagnes/${target.campaign_code}/deploiement?slot=${encodeURIComponent(target.slot)}&workflow_version_id=${versionId}${refresh ? "&refresh=true" : ""}`);
    };

    useEffect(() => {
        if (!open || !target) return;
        loadPreview();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, target?.campaign_code, target?.slot, versionId]);

    useEffect(() => {
        if (previewFetcher.state === "idle") setReplaying(false);
    }, [previewFetcher.state]);

    useEffect(() => {
        if (deployFetcher.state !== "idle" || !deployFetcher.data) return;
        if (deployFetcher.data.success) {
            toast.success(mode === "rollback" ? `Rolled back to version ${versionNumber}` : `Version ${versionNumber} deployed`);
            onOpenChange(false);
            onDone?.();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deployFetcher.state, deployFetcher.data]);

    const fetchedPreview = previewFetcher.data?.preview;
    // A fetcher retains its previous payload while the next target loads. Never show
    // another slot's "before" state during that interval.
    const preview =
        fetchedPreview && target && fetchedPreview.campaign_code === target.campaign_code && fetchedPreview.slot === target.slot && fetchedPreview.target_version.id === versionId
            ? fetchedPreview
            : undefined;
    const loading = previewFetcher.state !== "idle" && !preview;
    const submitting = deployFetcher.state !== "idle";
    const runningScenarios = replaying && previewFetcher.state !== "idle";

    const { blockers: effectiveBlockers, scenarioBlockers, canSubmit, mustRunScenarios } = deployGate(preview, mode, Boolean(target));

    const failure: unknown = deployFetcher.data && !deployFetcher.data.success ? deployFetcher.data.message : null;
    const failureBlockers: { code: string; message: string }[] =
        failure && typeof failure === "object" && "blockers" in failure && Array.isArray((failure as { blockers?: unknown }).blockers)
            ? (failure as { blockers: { code: string; message: string }[] }).blockers
            : [];

    const submit = () => {
        if (!target) return;
        deployFetcher.submit(
            {
                slot: target.slot,
                workflow_version_id: versionId,
                in_flight_policy: policy,
                ...(mode === "rollback" || switchingWorkflow ? { confirm: true } : {})
            },
            { method: "post", action: `/outils/campagnes/${target.campaign_code}/deploiement?action=${mode}`, encType: "application/json" }
        );
    };

    // The preview replays the scenarios itself when asked to refresh, so the dialog gets
    // the verdicts and the reopened — or still closed — gate in the same read.
    const runScenarios = () => loadPreview(true);

    const inFlight = preview?.in_flight;
    const currentVersion = preview?.current_version?.version;
    const currentWorkflowCode = preview?.current_version?.workflow_code;
    const sameWorkflow = Boolean(preview?.current_version) && (!currentWorkflowCode || currentWorkflowCode === workflowCode);
    const switchingWorkflow = Boolean(preview?.current_version) && !sameWorkflow;

    return (
        <Dialog open={open} onOpenChange={value => !submitting && onOpenChange(value)}>
            <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{mode === "rollback" ? `Roll back to version ${versionNumber}?` : `Deploy version ${versionNumber}`}</DialogTitle>
                    <DialogDescription>{workflowName || workflowCode}</DialogDescription>
                </DialogHeader>

                {targets.length === 0 ? (
                    <Alert variant="destructive">
                        <AlertTitle>No campaign to deploy into</AlertTitle>
                        <AlertDescription>
                            A version runs on a campaign slot, so there has to be a campaign first.{" "}
                            <Link className="underline" to="/dialer-campaigns">
                                Create one
                            </Link>
                            , then come back to this version.
                        </AlertDescription>
                    </Alert>
                ) : (
                    <div className="space-y-4">
                        {visibleTargets.length > 1 && (
                            <div className="space-y-1">
                                <Label className="text-xs text-gray-600">Campaign and slot</Label>
                                <Select value={selectedTarget || targetKey(targets[0])} onValueChange={setSelectedTarget}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {visibleTargets.map(candidate => (
                                            <SelectItem key={targetKey(candidate)} value={targetKey(candidate)}>
                                                {candidate.campaign_name || candidate.campaign_code} — {candidate.slot}
                                                {/* Which slot is free is the question the operator is answering
                                                    here, now that the free ones are offered at all. */}
                                                <span className="ml-1 text-xs text-gray-500">
                                                    {candidate.current
                                                        ? `· replaces ${candidate.current.workflow_code === workflowCode ? "" : `${candidate.current.workflow_name} `}v${candidate.current.version}`
                                                        : "· empty"}
                                                </span>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        {!showSwitchTargets && groupedTargets.switches.length > 0 && (
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    setShowSwitchTargets(true);
                                    if (!target) setSelectedTarget(targetKey(groupedTargets.switches[0]));
                                }}
                            >
                                Show slots running another workflow ({groupedTargets.switches.length})
                            </Button>
                        )}

                        {loading && (
                            <div className="space-y-2">
                                <Skeleton className="h-5 w-56" />
                                <Skeleton className="h-20 w-full" />
                            </div>
                        )}

                        {preview && (
                            <>
                                {effectiveBlockers.length > 0 && <FormError errorMessage={effectiveBlockers.map(blocker => blocker.message).join("\n")} />}
                                {failureBlockers.length > 0 && <FormError errorMessage={failureBlockers.map(blocker => blocker.message).join("\n")} />}
                                {failure && typeof failure === "string" && <FormError errorMessage={failure} />}
                                {switchingWorkflow && (
                                    <Alert variant="warning">
                                        <AlertTriangle className="h-4 w-4" />
                                        <AlertTitle>You are switching workflows</AlertTitle>
                                        <AlertDescription>
                                            {preview.current_version?.workflow_name || preview.current_version?.workflow_code} v{currentVersion} on slot {preview.slot} will be replaced by{" "}
                                            {workflowName || workflowCode} v{versionNumber}. Cards already running stay on the previous graph unless you migrate them.
                                        </AlertDescription>
                                    </Alert>
                                )}

                                <Section title="What changes">
                                    <p className="text-sm">
                                        {currentVersion == null
                                            ? `No version currently deployed → v${versionNumber}`
                                            : sameWorkflow
                                              ? `v${currentVersion} → v${versionNumber}`
                                              : `${preview.current_version?.workflow_name || preview.current_version?.workflow_code} v${currentVersion} → ${workflowName || workflowCode} v${versionNumber}`}{" "}
                                        on slot <span className="font-mono">{preview.slot}</span>
                                    </p>
                                    {currentVersion != null && currentVersion !== versionNumber && sameWorkflow && (
                                        <Link
                                            className="self-start text-sm text-blue-600 hover:text-blue-800 hover:underline"
                                            to={`/outils/campagnes/workflows/${workflowCode}/v/${versionNumber}/comparer/${currentVersion}`}
                                        >
                                            See the full comparison
                                        </Link>
                                    )}
                                    {currentVersion != null && !sameWorkflow && (
                                        <p className="text-xs text-gray-500">Graph comparison is unavailable because this slot currently uses another workflow.</p>
                                    )}
                                </Section>

                                <Section title="The fate of cards already running">
                                    <RadioGroup value={policy} onValueChange={value => setPolicy(value as InFlightPolicy)}>
                                        <div className="flex items-start gap-2">
                                            <RadioGroupItem value="pin" id="policy-pin" />
                                            <Label htmlFor="policy-pin" className="text-sm font-normal">
                                                Let the {inFlight?.active_cards ?? 0} active cards finish on version {currentVersion ?? "the current one"}
                                                <span className="block text-xs text-gray-500">The safe choice: nothing already in flight changes behaviour.</span>
                                            </Label>
                                        </div>
                                        <div className="flex items-start gap-2">
                                            <RadioGroupItem value="migrate" id="policy-migrate" />
                                            <Label htmlFor="policy-migrate" className="text-sm font-normal">
                                                Move active cards to version {versionNumber} at their next step
                                                <span className="block text-xs text-gray-500">
                                                    {inFlight?.migratable ?? 0} cards can move; {inFlight?.not_migratable ?? 0} are at a step that no longer exists in version {versionNumber} and will
                                                    finish on version {currentVersion ?? "the current one"} whatever you choose.
                                                </span>
                                            </Label>
                                        </div>
                                    </RadioGroup>
                                    {(inFlight?.missing_node_ids ?? []).length > 0 && (
                                        <div>
                                            <Button type="button" variant="ghost" size="sm" className="h-6 px-1 text-xs" onClick={() => setShowMissing(value => !value)}>
                                                {showMissing ? <ChevronDown className="mr-1 h-4 w-4" /> : <ChevronRight className="mr-1 h-4 w-4" />}
                                                Steps that disappeared ({inFlight?.missing_node_ids.length})
                                            </Button>
                                            {showMissing && (
                                                <ul className="ml-4 list-disc font-mono text-xs text-gray-600">{inFlight?.missing_node_ids.map(nodeId => <li key={nodeId}>{nodeId}</li>)}</ul>
                                            )}
                                        </div>
                                    )}
                                </Section>

                                <Section title="Guardrails" actions={<ScenarioPill summary={preview.scenarios} />}>
                                    {preview.scenarios.total === 0 && (
                                        <Alert variant="warning">
                                            <AlertTriangle className="h-4 w-4" />
                                            <AlertTitle>No scenario covers this version</AlertTitle>
                                            <AlertDescription>
                                                Deployment is allowed, but nothing proves the workflow behaves as intended. Record a scenario from the simulator first if you can.
                                            </AlertDescription>
                                        </Alert>
                                    )}
                                    {(preview.warnings ?? [])
                                        .filter(warning => warning.code !== "workflow_switch")
                                        .map(warning => (
                                            <Alert key={warning.code} variant="warning">
                                                <AlertTriangle className="h-4 w-4" />
                                                <AlertDescription>{warning.message}</AlertDescription>
                                            </Alert>
                                        ))}
                                    {mode === "rollback" && scenarioBlockers.length > 0 && (
                                        <Alert variant="info">
                                            <Info className="h-4 w-4" />
                                            <AlertDescription>Scenarios currently fail on this version, but it already ran in production: the rollback is allowed and will be logged.</AlertDescription>
                                        </Alert>
                                    )}
                                </Section>
                            </>
                        )}
                    </div>
                )}

                <DialogFooter className="gap-2">
                    <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
                        Cancel
                    </Button>
                    {mustRunScenarios ? (
                        <Button type="button" onClick={runScenarios} disabled={runningScenarios}>
                            {runningScenarios ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
                            Run scenarios
                        </Button>
                    ) : (
                        <Button type="button" onClick={submit} disabled={!canSubmit || submitting}>
                            {submitting ? "Applying…" : mode === "rollback" ? "Roll back" : switchingWorkflow ? "Confirm workflow switch" : "Deploy"}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
